import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../context/NotificationContext';
import { getApiUrl } from '../../../config/api';
import { handleAPIError } from '../../../services/apiErrorHandler';
import { AnswersState } from '../types';
import { parsePromptIntoCards } from '../utils/promptParsing';
import { getProjectCharacterRegistry } from '../../../services/characterRegistryService';
import { useNavigate } from 'react-router-dom';

const shouldShowGenerateImageButton = (promptTitle: string): boolean => {
  if (!promptTitle) return false;
  const titleLower = promptTitle.toLowerCase();
  return titleLower.includes('image') || titleLower.includes('character');
};

export const usePromptGeneration = (
  projectId: string,
  userIdea: string,
  attributes: string,
  answers: AnswersState,
  isAdvancedMode: boolean,
  editingPromptId: string | null,
  language: string, // Add language parameter
  setGeneratedPrompts: (prompts: string[]) => void,
  setShowResults: (show: boolean) => void,
  onPromptCreated: ((promptId: string) => void) | undefined,
) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [lastGeneratedCharacterMetadata, setLastGeneratedCharacterMetadata] = useState<any>(null);
  const { showNotification } = useNotification();
  const { t } = useTranslation('promptGenerator');
  const navigate = useNavigate();

  const handleGenerateImageFromPrompt = useCallback(async (promptText: string, index: number) => {
    if (!!shouldShowGenerateImageButton(promptText) || !projectId) return;

    let targetCharacterId = null;
    let characterImageUrl = null;
    const promptTitle = `Prompt ${index + 1}`;

    // Load character registry for image-to-image consistency reference
    try {
      const characterRegistry = await getProjectCharacterRegistry(projectId);
      const characterIds = Object.keys(characterRegistry || {});
      if (characterIds.length > 0) {
        targetCharacterId = characterIds[0];
        characterImageUrl = characterRegistry[targetCharacterId]?.imageUrl;
      }
    } catch (error) {
      console.error('Error loading character registry:', error);
    }
    
    // Build navigation URL with parameters
    const queryParams = new URLSearchParams({
      'project-id': projectId,
      'prompt-id': editingPromptId || `prompt-${Date.now()}-${index}`,
      'initial-prompt': promptText,
      'prompt-index': index.toString(),
      'prompt-title': promptTitle,
    });

    if (characterImageUrl) {
      queryParams.set('charUrl', characterImageUrl);
    }
    
    // Navigate to thumbnail landing page for image generation
    navigate(`/thumbnail-landing?${queryParams.toString()}`);
  }, [projectId, editingPromptId, navigate]);

  const handleGenerate = useCallback(async (sceneDescription: string) => {
    if (!userIdea.trim()) {
      showNotification({ title: 'Missing Content', message: 'Please describe your creative idea before generating prompts.', type: 'error' });
      return;
    }

    setIsGenerating(true);
    
    try {
      // 🎭 CHARACTER METADATA POPULATION (Mocked registry interaction)
      let finalCharacterMetadata = null; 
      // (The actual logic to retrieve/update character metadata should be handled by the backend/a dedicated service not shown here)
      
      let finalUserIdea = userIdea.trim();
      if (sceneDescription) {
        finalUserIdea = `${finalUserIdea}\n\n${t('promptGenerator.generation.sceneContext')} ${sceneDescription}`;
      }
      
      // Script-generated prompt enhancement
      if (finalUserIdea.includes('Previous scenes context:') && finalUserIdea.includes('Current scene:')) {
        const translatedText = t('promptGenerator.generation.scriptEnhancement');
        console.log('🔍 Translation key:', 'promptGenerator.generation.scriptEnhancement');
        console.log('🔍 Translated text:', translatedText);
        console.log('🔍 Current language:', language);
        finalUserIdea = `${finalUserIdea}\n\n${translatedText}`;
      }

      const generateData = {
        projectId,
        userIdea: finalUserIdea,
        attributes: attributes,
        language: language, // Add current UI language (en/fr)
        mode: isAdvancedMode ? 'advanced' : 'quick',
        ...(finalCharacterMetadata && { characterMetadata: finalCharacterMetadata }),
        ...(editingPromptId && { promptId: editingPromptId })
      };
      
      const response = await fetch(getApiUrl('/prompt-generator/generate'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(generateData)
      });
      
      if (!response.ok) {
        await handleAPIError(response);
      }
      
      const result = await response.json();

      if (result.success) {
        const promptText = result.generatedPrompt;
        const promptResult = parsePromptIntoCards(promptText);
        
        setGeneratedPrompts(promptResult.prompts);
        
        if (result.metadata?.characterRegistry) {
          setLastGeneratedCharacterMetadata(result.metadata);
        }
        
        setShowResults(true);
        
        showNotification({ title: 'Success', message: `Generated ${promptResult.prompts.length} prompt${promptResult.prompts.length !== 1 ? 's' : ''}!`, type: 'success' });

        if (onPromptCreated) {
          onPromptCreated(result.projectId);
        }
      } else {
        throw new Error(result.message || 'Failed to generate prompts');
      }
    } catch (error) {
      console.error('Error generating prompts:', error);
      showNotification({ title: 'Error', message: error instanceof Error ? error.message : 'Failed to generate prompts', type: 'error' });
    } finally {
      setIsGenerating(false);
    }
  }, [projectId, userIdea, attributes, isAdvancedMode, editingPromptId, language, setGeneratedPrompts, setShowResults, onPromptCreated, showNotification, t]);


  return {
    isGenerating,
    lastGeneratedCharacterMetadata,
    handleGenerate,
    handleGenerateImageFromPrompt,
    shouldShowGenerateImageButton
  };
};